'use client';

import React, { useState, useEffect, useRef } from 'react';
import Sidebar from './Sidebar';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

interface AppLayoutProps {
  children: React.ReactNode;
  currentPath?: string;
}

export default function AppLayout({ children, currentPath = '' }: AppLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [editName, setEditName] = useState({ prenom: '', nom: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [editMsg, setEditMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  const { userProfile, signOut, user, refreshProfile } = useAuth();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarCollapsed(true);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close profile menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target as Node)) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSignOut = async () => {
    try {
      setProfileMenuOpen(false);
      await signOut();
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  const handleOpenEditProfile = () => {
    setEditName({
      prenom: userProfile?.prenom || '',
      nom: userProfile?.nom || '',
    });
    setEditMsg(null);
    setEditProfileOpen(true);
    setProfileMenuOpen(false);
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    setEditSaving(true);
    setEditMsg(null);
    try {
      const supabase = createClient();
      const { error } = await supabase
        .from('user_profiles')
        .update({ prenom: editName.prenom.trim(), nom: editName.nom.trim() })
        .eq('id', user.id);
      if (error) throw error;
      await refreshProfile();
      setEditMsg({ type: 'success', text: 'Profil mis à jour avec succès.' });
      setTimeout(() => setEditProfileOpen(false), 1200);
    } catch (e: any) {
      setEditMsg({ type: 'error', text: e?.message || 'Erreur lors de la mise à jour.' });
    } finally {
      setEditSaving(false);
    }
  };

  const displayName = userProfile
    ? `${userProfile.prenom} ${userProfile.nom}`.trim() || userProfile.email
    : 'Visiteur';
  const displayRole = userProfile?.role === 'Administrateur' ? 'Administrateur' : userProfile?.role ? 'Utilisateur' : '';
  const initials = userProfile
    ? `${userProfile.prenom?.[0] || ''}${userProfile.nom?.[0] || ''}`.toUpperCase() || 'U'
    : 'V';

  const ROLE_BADGE_STYLES: Record<string, string> = {
    'Administrateur': 'bg-red-100 text-red-700',
    'Utilisateur': 'bg-slate-100 text-slate-500',
    'Visiteur': 'bg-slate-100 text-slate-500',
  };
  const roleBadgeStyle = displayRole ? (ROLE_BADGE_STYLES[displayRole] || 'bg-slate-100 text-slate-500') : '';

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
        currentPath={currentPath}
      />

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden transition-all duration-300">
        {/* Top bar */}
        <header className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6 flex-shrink-0 z-10">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
              aria-label="Ouvrir le menu"
            >
              <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="hidden lg:flex items-center gap-2">
              <span className="text-xs font-500 text-slate-400 uppercase tracking-widest">Portail Pro</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="hidden md:flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-2 w-56 cursor-pointer hover:bg-slate-200 transition-colors">
              <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <span className="text-sm text-slate-400">Recherche rapide...</span>
              <span className="ml-auto text-xs text-slate-300 font-mono">⌘K</span>
            </div>

            {/* User avatar with dropdown */}
            <div className="relative flex items-center gap-2.5 pl-2 border-l border-slate-200" ref={profileMenuRef}>
              <button
                onClick={() => setProfileMenuOpen(o => !o)}
                className="flex items-center gap-2.5 rounded-xl px-2 py-1 hover:bg-slate-100 transition-colors"
                title="Mon profil"
              >
                <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-600 flex-shrink-0">
                  {initials}
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-600 text-slate-800 leading-none">{displayName}</p>
                  {displayRole && (
                    <span className={`inline-block text-[10px] font-600 px-1.5 py-0.5 rounded mt-0.5 leading-none ${roleBadgeStyle}`}>
                      {displayRole}
                    </span>
                  )}
                </div>
                <svg className="w-3.5 h-3.5 text-slate-400 hidden md:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Profile dropdown menu */}
              {profileMenuOpen && (
                <div className="absolute right-0 top-12 z-50 bg-white border border-slate-200 rounded-xl shadow-xl w-64 py-2">
                  {/* User info header */}
                  <div className="px-4 py-3 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-600 flex-shrink-0">
                        {initials}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-600 text-slate-900 truncate">{displayName}</p>
                        <p className="text-xs text-slate-500 truncate">{userProfile?.email || ''}</p>
                        {displayRole && (
                          <span className={`inline-block text-[10px] font-600 px-1.5 py-0.5 rounded mt-0.5 leading-none ${roleBadgeStyle}`}>
                            {displayRole}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Menu items */}
                  <div className="py-1">
                    <button
                      onClick={handleOpenEditProfile}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Mon profil
                    </button>
                    <button
                      onClick={handleOpenEditProfile}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Modifier mes informations
                    </button>
                  </div>

                  {userProfile && (
                    <div className="border-t border-slate-100 py-1">
                      <button
                        onClick={handleSignOut}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Se déconnecter
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto scrollbar-thin">
          {children}
        </main>
      </div>

      {/* Edit Profile Modal */}
      {editProfileOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setEditProfileOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Modifier mon profil</h2>
                <p className="text-sm text-slate-500 mt-0.5">Mettez à jour vos informations personnelles</p>
              </div>
              <button onClick={() => setEditProfileOpen(false)} className="p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Avatar preview */}
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                <div className="w-14 h-14 rounded-full bg-blue-600 flex items-center justify-center text-white text-lg font-600">
                  {`${editName.prenom?.[0] || ''}${editName.nom?.[0] || ''}`.toUpperCase() || 'U'}
                </div>
                <div>
                  <p className="text-sm font-600 text-slate-800">{editName.prenom} {editName.nom}</p>
                  <p className="text-xs text-slate-500">{userProfile?.email}</p>
                  {displayRole && (
                    <span className={`inline-block text-[10px] font-600 px-1.5 py-0.5 rounded mt-1 leading-none ${roleBadgeStyle}`}>
                      {displayRole}
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Prénom</label>
                  <input
                    type="text"
                    value={editName.prenom}
                    onChange={e => setEditName(n => ({ ...n, prenom: e.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Prénom"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Nom</label>
                  <input
                    type="text"
                    value={editName.nom}
                    onChange={e => setEditName(n => ({ ...n, nom: e.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Nom"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Email</label>
                <input
                  type="email"
                  value={userProfile?.email || ''}
                  disabled
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm bg-slate-50 text-slate-400 cursor-not-allowed"
                />
                <p className="text-xs text-slate-400 mt-1">L'email ne peut pas être modifié ici</p>
              </div>

              {editMsg && (
                <div className={`p-3 rounded-xl text-sm ${editMsg.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  {editMsg.text}
                </div>
              )}

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setEditProfileOpen(false)}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleSaveProfile}
                  disabled={editSaving}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {editSaving ? (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : null}
                  {editSaving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}